/**
 * Upload limits and the validation that enforces them, in one place because
 * four areas share three of the four numbers and drift between them is what
 * produced the inconsistent messages this replaces.
 *
 * Limits are checked before submitting, and the same vocabulary is used for
 * the backend's own rejections so both paths produce identical copy.
 */

/**
 * Decimal MB, matching the copy ("25 MB"), the app's existing caps and the
 * dropzone's own `bytes / 1e6` hint. It is also the stricter reading: a file
 * the client accepts is never one a MiB-based server would refuse.
 */
const MB = 1_000_000;

export interface UploadLimits {
  /** Files per request, counted across every selection made for that request. */
  maxFiles: number;
  maxFileSize: number;
  maxTotalSize: number;
}

const STANDARD_LIMITS: UploadLimits = {
  maxFiles: 20,
  maxFileSize: 25 * MB,
  maxTotalSize: 100 * MB,
};

/** Knowledge Hub takes bigger single documents, same total. */
const KNOWLEDGE_HUB_LIMITS: UploadLimits = {
  maxFiles: 20,
  maxFileSize: 100 * MB,
  maxTotalSize: 100 * MB,
};

export const UPLOAD_LIMITS = {
  chat: STANDARD_LIMITS,
  farmProfile: STANDARD_LIMITS,
  plot: STANDARD_LIMITS,
  knowledgeHub: KNOWLEDGE_HUB_LIMITS,
} as const;

export type UploadArea = keyof typeof UPLOAD_LIMITS;

/**
 * Areas left as they were: a single file each, with their own size caps.
 * Listed so a future change can see them rather than assume the shared limits
 * apply everywhere.
 */
export const SINGLE_FILE_LIMITS = {
  bulkImport: { maxFiles: 1, maxFileSize: 100 * MB, maxTotalSize: 100 * MB },
  speechToText: { maxFiles: 1, maxFileSize: 25 * MB, maxTotalSize: 25 * MB },
  irrigationImage: { maxFiles: 1, maxFileSize: 5 * MB, maxTotalSize: 5 * MB },
} as const satisfies Record<string, UploadLimits>;

/** The backend's stable codes, plus the one the client decides on its own. */
export const UPLOAD_ERROR_CODES = {
  tooManyFiles: 'UPLOAD_TOO_MANY_FILES',
  fileTooLarge: 'UPLOAD_FILE_TOO_LARGE',
  totalSizeExceeded: 'UPLOAD_TOTAL_SIZE_EXCEEDED',
} as const;

export type UploadErrorCode =
  (typeof UPLOAD_ERROR_CODES)[keyof typeof UPLOAD_ERROR_CODES];

export interface UploadValidationError {
  code: UploadErrorCode;
  /** The file that broke the rule, when one file is to blame. */
  fileName?: string;
}

interface SizedFile {
  name: string;
  size: number;
}

/**
 * Checks a whole request, not one selection: pass every file destined for the
 * request, including any already staged from an earlier pick.
 *
 * Files exactly at a limit are accepted — the limits are inclusive.
 */
export function validateUploadSelection(
  files: readonly SizedFile[],
  limits: UploadLimits
): UploadValidationError | undefined {
  if (files.length > limits.maxFiles) {
    return { code: UPLOAD_ERROR_CODES.tooManyFiles };
  }

  const tooLarge = files.find((file) => file.size > limits.maxFileSize);
  if (tooLarge) {
    return { code: UPLOAD_ERROR_CODES.fileTooLarge, fileName: tooLarge.name };
  }

  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > limits.maxTotalSize) {
    return { code: UPLOAD_ERROR_CODES.totalSizeExceeded };
  }

  return undefined;
}

/** Whole megabytes, for copy that quotes a limit ("exceeds the 25 MB limit"). */
export const limitInMb = (bytes: number) => Math.round(bytes / MB);

interface ApiErrorLike {
  errorCode?: string;
  message?: string;
  data?: unknown;
}

/**
 * Reads a rejection the server sent. Branch on `errorCode`; the message is
 * prose that changes. `apiFetch` puts the code in `message` too when it has
 * one, which is why both are consulted.
 */
export function uploadErrorFromApi(
  error: unknown
): UploadValidationError | undefined {
  const candidate = error as ApiErrorLike | undefined;
  const code = candidate?.errorCode ?? candidate?.message;
  if (
    code !== UPLOAD_ERROR_CODES.fileTooLarge &&
    code !== UPLOAD_ERROR_CODES.totalSizeExceeded
  ) {
    return undefined;
  }
  // `data.file` names the offending file when the server knows which it was.
  const data = candidate?.data as { file?: string; fileName?: string } | undefined;
  const fileName = data?.file ?? data?.fileName;
  return { code, ...(fileName ? { fileName } : {}) };
}

/**
 * The message an upload failure should show, as a key and its values, so the
 * hook and the global mutation handler render identical copy from their own
 * translator. Keys live in the `validation` namespace.
 */
export function uploadErrorMessage(
  error: UploadValidationError,
  area: UploadArea
): { key: string; values: Record<string, string | number> } {
  const limits = UPLOAD_LIMITS[area];
  switch (error.code) {
    case UPLOAD_ERROR_CODES.tooManyFiles:
      return {
        key: 'uploadTooManyFiles',
        values: { maxFiles: limits.maxFiles },
      };
    case UPLOAD_ERROR_CODES.fileTooLarge:
      return error.fileName
        ? {
            key: 'uploadFileTooLarge',
            values: {
              fileName: error.fileName,
              maxSize: limitInMb(limits.maxFileSize),
            },
          }
        : {
            key: 'uploadFileTooLargeUnnamed',
            values: { maxSize: limitInMb(limits.maxFileSize) },
          };
    case UPLOAD_ERROR_CODES.totalSizeExceeded:
    default:
      return {
        key: 'uploadTotalSizeExceeded',
        values: { maxTotal: limitInMb(limits.maxTotalSize) },
      };
  }
}
